// The live-presence socket lifecycle: mint a single-use ticket, open the ws,
// reconnect with capped exponential backoff + jitter (re-minting a ticket every
// attempt), reply to keep-alive pings, and hand typed frames to a listener.
// Framework-free and fully injectable (ticket minter, socket factory, scheduler,
// RNG) so the backoff schedule + presence handling are unit testable with a fake
// WebSocket. When anything fails, the UI simply stays on its REST polling — the
// socket is an enhancement, never a dependency.

import { parseInbound, type InboundMessage } from './ws-messages.ts';

// Backoff bounds (docs: reconnect 1s → 60s with jitter).
export const BACKOFF_BASE_MS = 1000;
export const BACKOFF_MAX_MS = 60_000;

/**
 * Capped exponential backoff with equal jitter for reconnect attempt `n`
 * (0-based). Base doubles each attempt (1s, 2s, 4s, …) capped at 60s; the actual
 * delay is half the cap plus a random half — so attempt 0 lands in [0.5s, 1s],
 * attempt 1 in [1s, 2s], … never exceeding 60s. `rand` is injectable for tests.
 */
export function backoffDelay(attempt: number, rand: () => number = Math.random): number {
  const capped = Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * 2 ** Math.max(0, attempt));
  const half = capped / 2;
  return Math.round(half + rand() * half);
}

/** Derive the absolute ws(s) URL from the REST API base (which ends in `/v1`).
 *  Relative bases (`/v1` in dev/tests) resolve against the current page origin;
 *  absolute https bases (prod) swap to wss. The endpoint is `<base>/ws?ticket=`. */
export function wsUrlFromApiBase(apiBase: string, ticket: string, origin?: string): string {
  const pageOrigin =
    origin ?? (typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
  const absolute = apiBase.startsWith('/') ? `${pageOrigin}${apiBase}` : apiBase;
  const u = new URL(absolute);
  u.protocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
  u.pathname = `${u.pathname.replace(/\/$/, '')}/ws`;
  u.search = `?ticket=${encodeURIComponent(ticket)}`;
  return u.toString();
}

/** The subset of the WebSocket API the socket drives (native WebSocket satisfies
 *  it structurally; tests pass a fake). */
export interface WebSocketLike {
  readyState: number;
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: ((ev: unknown) => void) | null;
  onclose: ((ev: unknown) => void) | null;
  onerror: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
}

export type LiveStatus = 'connecting' | 'open' | 'reconnecting' | 'stopped';

/** A minimal connection the presence provider drives — the real socket and the
 *  demo scripted emitter both implement it, so the provider stays agnostic. */
export interface LiveConnection {
  start(): void;
  stop(): void;
}

export interface LiveSocketDeps {
  /** POST /v1/ws-ticket → a fresh single-use ticket. Called on every attempt. */
  mintTicket: (signal?: AbortSignal) => Promise<string>;
  /** Build the connect URL for a ticket (see wsUrlFromApiBase). */
  wsUrl: (ticket: string) => string;
  /** Deliver a typed inbound frame (ping is handled internally, not delivered). */
  onMessage: (msg: InboundMessage) => void;
  /** Connection-status changes, for an optional UI indicator. */
  onStatus?: (status: LiveStatus) => void;
  /** Open a socket. Defaults to the native WebSocket. */
  createSocket?: (url: string) => WebSocketLike;
  /** Jitter source (tests inject determinism). */
  rand?: () => number;
  /** Deferred scheduler returning a canceller. Defaults to setTimeout/clearTimeout. */
  schedule?: (fn: () => void, ms: number) => () => void;
}

export class LiveSocket implements LiveConnection {
  private stopped = true;
  private attempt = 0;
  private socket: WebSocketLike | null = null;
  private cancelReconnect: (() => void) | null = null;
  private abort: AbortController | null = null;

  private readonly rand: () => number;
  private readonly schedule: (fn: () => void, ms: number) => () => void;
  private readonly createSocket: (url: string) => WebSocketLike;

  constructor(private readonly deps: LiveSocketDeps) {
    this.rand = deps.rand ?? Math.random;
    this.schedule =
      deps.schedule ??
      ((fn, ms) => {
        const id = setTimeout(fn, ms);
        return () => clearTimeout(id);
      });
    this.createSocket =
      deps.createSocket ?? ((url) => new WebSocket(url) as unknown as WebSocketLike);
  }

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    this.attempt = 0;
    void this.connect();
  }

  stop(): void {
    this.stopped = true;
    this.cancelReconnect?.();
    this.cancelReconnect = null;
    this.abort?.abort();
    this.abort = null;
    if (this.socket) {
      this.detach(this.socket);
      try {
        this.socket.close();
      } catch {
        /* ignore */
      }
      this.socket = null;
    }
    this.setStatus('stopped');
  }

  /** Backoff delay that WOULD be used for the next attempt — exposed for tests. */
  nextDelay(): number {
    return backoffDelay(this.attempt, this.rand);
  }

  private setStatus(status: LiveStatus): void {
    this.deps.onStatus?.(status);
  }

  private detach(sock: WebSocketLike): void {
    sock.onopen = null;
    sock.onclose = null;
    sock.onerror = null;
    sock.onmessage = null;
  }

  private async connect(): Promise<void> {
    if (this.stopped) return;
    this.setStatus(this.attempt === 0 ? 'connecting' : 'reconnecting');
    this.abort = new AbortController();
    let ticket: string;
    try {
      ticket = await this.deps.mintTicket(this.abort.signal);
    } catch {
      return this.scheduleReconnect();
    }
    if (this.stopped) return;

    let sock: WebSocketLike;
    try {
      sock = this.createSocket(this.deps.wsUrl(ticket));
    } catch {
      return this.scheduleReconnect();
    }
    this.socket = sock;

    sock.onopen = () => {
      if (this.stopped) return;
      this.attempt = 0;
      this.setStatus('open');
    };
    sock.onmessage = (ev) => {
      const msg = parseInbound(ev.data);
      if (!msg) return;
      if (msg.type === 'ping') {
        this.sendPong(sock);
        return;
      }
      this.deps.onMessage(msg);
    };
    sock.onclose = () => {
      if (this.socket === sock) this.socket = null;
      this.detach(sock);
      if (!this.stopped) this.scheduleReconnect();
    };
    sock.onerror = () => {
      // A close event follows an error; reconnect is driven from onclose so we
      // don't schedule twice. Nothing to do here.
    };
  }

  private sendPong(sock: WebSocketLike): void {
    try {
      sock.send(JSON.stringify({ type: 'pong' }));
    } catch {
      /* a dead socket will surface via onclose */
    }
  }

  private scheduleReconnect(): void {
    if (this.stopped || this.cancelReconnect) return;
    const delay = backoffDelay(this.attempt, this.rand);
    this.attempt += 1;
    this.setStatus('reconnecting');
    this.cancelReconnect = this.schedule(() => {
      this.cancelReconnect = null;
      void this.connect();
    }, delay);
  }
}
